import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

import { cn } from '../../lib/cn';
import styles from './Select.module.css';

export type SelectOption<T extends string> = {
  readonly value: T;
  readonly label: string;
  readonly tone?: string;
  readonly description?: string;
};

export type SelectProps<T extends string> = {
  readonly options: readonly SelectOption<T>[];
  readonly value: T | null;
  readonly onChange: (value: T) => void;
  readonly placeholder?: string;
  readonly label?: string;
  readonly error?: string | null;
  readonly disabled?: boolean;
  readonly clearable?: boolean;
  readonly onClear?: () => void;
};

const SEARCH_THRESHOLD = 8;

/** Кастомный селект: с поиском, если опций больше восьми (docs/06 §4). */
export function Select<T extends string>({
  options,
  value,
  onChange,
  placeholder = 'Выбрать',
  label,
  error,
  disabled = false,
  clearable = false,
  onClear,
}: SelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);

  const showSearch = options.length > SEARCH_THRESHOLD;
  const selected = options.find((option) => option.value === value) ?? null;
  const filtered = useMemo(() => {
    if (!showSearch || query.trim().length === 0) {
      return options;
    }
    const needle = query.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query, showSearch]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div className={styles['field']} ref={rootRef}>
      {label ? <span className={styles['label']}>{label}</span> : null}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-invalid={error ? true : undefined}
        className={cn(styles['trigger'], error && styles['invalid'], isOpen && styles['open'])}
        onClick={() => setIsOpen((open) => !open)}
      >
        {selected?.tone ? (
          <span aria-hidden className={styles['tone']} style={{ background: selected.tone }} />
        ) : null}
        <span className={cn(styles['value'], !selected && styles['placeholder'])}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown aria-hidden size={16} className={cn(styles['chevron'], isOpen && styles['chevronOpen'])} />
      </button>

      {isOpen ? (
        <div className={styles['popover']}>
          {showSearch ? (
            <div className={styles['search']}>
              <Search aria-hidden size={14} />
              <input
                autoFocus
                value={query}
                placeholder="Поиск"
                aria-label="Поиск по опциям"
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          ) : null}
          <ul className={styles['list']} id={listId} role="listbox">
            {clearable ? (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={value === null}
                  className={styles['option']}
                  onClick={() => {
                    onClear?.();
                    setIsOpen(false);
                  }}
                >
                  <span className={styles['optionLabel']}>Не задано</span>
                </button>
              </li>
            ) : null}
            {filtered.length === 0 ? (
              <li className={styles['noOptions']}>Ничего не найдено</li>
            ) : (
              filtered.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    className={cn(styles['option'], option.value === value && styles['optionActive'])}
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                  >
                    {option.tone ? (
                      <span aria-hidden className={styles['tone']} style={{ background: option.tone }} />
                    ) : null}
                    <span className={styles['optionLabel']}>
                      {option.label}
                      {option.description ? <small>{option.description}</small> : null}
                    </span>
                    {option.value === value ? <Check aria-hidden size={14} /> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}

      {error ? (
        <p className={styles['error']} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
